import i18n from '@/locale'
import Vue from 'vue'
import router from '@/router'
import store from '@/store'
import {
  successCodes,
  ignoreCodes,
  loginBackInCodes,
  cacheWhiteList,
  speechApiLists,
  ignoreCheckUpdateWhiteList,
} from '@/config'
import { AxiosError } from 'axios'
import { HttpStatusCode } from '@/constants'
import Speech from 'speak-tts'
import { AppVersionStorage, LocaleStorage } from '@/utils/storage'
import { MessageBox } from 'element-ui'
// 判断版本更新弹框是否已显示
let isVersionDialogShown = false

/** 是否跳过全局错误弹窗（全局 ignoreCodes + 单次请求 config.ignoreCodes） */
function shouldIgnoreErrorDialog(code, config) {
  const requestIgnoreCodes = config?.ignoreCodes
  if (Array.isArray(requestIgnoreCodes) && requestIgnoreCodes.includes(code)) {
    return true
  }
  return ignoreCodes.includes(code)
}
/**
 * 关闭loading
 * @param {*} config
 */
function closeLoading(config) {
  if (config?.loading) {
    setTimeout(() => {
      if (config?.loadingInstance) {
        config.loadingInstance.close()
        config.loadingInstance = null
      }
    })
  }
}

// 语音播报初始化
function handleSpeechInit() {
  return new Promise((resolve, reject) => {
    let speech = new Speech()
    speech.setVolume(1)
    if (!speech?.browserSupport)
      return Vue.prototype.$mgMessage(i18n.t('该浏览器暂不支持语音播报, 建议使用【谷歌】浏览器'), 'error')
    speech.setLanguage(LocaleStorage.get())
    speech
      .init()
      .then(() => {
        resolve(speech)
      })
      .catch((e) => {
        reject(e)
      })
  })
}
// 语音播报
function handleErrorVoiceSpeak(msg) {
  if (!msg) return
  handleSpeechInit().then((res) => {
    res.speak({ text: msg }).then(() => {})
  })
}
/**
 * 检查版本是否需要更新
 * @param {*} response
 */
export function checkAppVersion(response) {
  if (!response?.headers) return
  // 过滤不需要检查版本号的接口 原来逻辑
  if (ignoreCheckUpdateWhiteList.includes(response.config.url)) {
    return
  }

  //登录接口过滤掉 2025/10/27 新增
  if (response.config.url.indexOf('/mg/drp-user-api/userAccount/login') !== -1) {
    return
  }

  //查看店铺接口 2025/10/27 新增
  if (response.config.url.indexOf('/mg/daohang-basic-api/employee/currentRelevanceShop') !== -1) {
    if (response.data?.data?.length > 0) {
      //只要有店铺就跳过
      return
    }
  }

  const appVersion = response.headers['app-version']
  if (appVersion) {
    const localAppVersion = AppVersionStorage.get()
    if (!localAppVersion) {
      AppVersionStorage.set(appVersion)
      return
    }
    if (localAppVersion != appVersion) {
      if (isVersionDialogShown) return
      isVersionDialogShown = true
      // alert(i18n.t('检测到当前系统版本已更新，请刷新页面！'))
      MessageBox.confirm(i18n.t('检测到当前系统版本已更新，请刷新页面！'), i18n.t('提示'), {
        confirmButtonText: i18n.t('确定[ENTER]'),
        showCancelButton: false,
        showClose: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        type: 'warning',
      })
        .then(() => {
          AppVersionStorage.set(appVersion)
          window.location.reload()
        })
        .finally(() => {
          isVersionDialogShown = false
        })
    }
  }
}

/**
 * 状态码为 2xx 范围内都会触发此方法
 * @param {*} response
 * @returns
 */
export function handleResponseSuccess(response) {
  const { data, config, headers } = response
  const { code } = data

  // 关闭 loading
  closeLoading(config)

  checkAppVersion(response)

  // 处理文件流
  if (config.responseType === 'blob') {
    // 对象有 'content-disposition' 属性 一定是原始导出，接口返回的是文件流
    if (Object.keys(headers).includes('content-disposition')) {
      return response
    } else {
      //否则执行下载中心导出
      return { code: 200, flag: 'downloadCenter' }
    }
  }

  // 处理接口正常返回数据
  if (successCodes.includes(code)) {
    if (cacheWhiteList?.includes(config.url)) {
      LocalForage.setItem(config.url, data.data)
    }
    // 修复：在返回之前清理队列，避免因提前返回导致队列阻塞
    if (config.headers?.['x-promotion-uid'])
      store.dispatch('promotionReqQueue/actRemoveRequest', config.headers['x-promotion-uid'])
    if (headers?.['api-query-verison']) return { ...data, headers }
    return data
  }
  // 处理其他异常情况，使用自定义错误码
  response.status = HttpStatusCode.NO_SUCCESS
  return handleResponseError({ response })
}
/**
 * 状态码超出 2xx 范围会触发此方法
 *  status http状态码
 *  code   接口返回的特定状态码
 */
var httpErrorDialogCanShow = true //系统错误弹窗是否可以显示

export function handleResponseError({ code: cancelCode, message, response }) {
   let isOnline = true
  if (navigator.onLine) {
    // console.log('TIPS 网络在线')
    isOnline = true
  } else {
    // console.log('TIPS 网络离线')
    isOnline = false
  }

  if (!response) {
    response = { data: { code: cancelCode, msg: message } }
  }
  // 关闭 loading
  closeLoading(response.config || { loading: true })

  // 处理取消重复请求的错误
  if (cancelCode === AxiosError.ERR_CANCELED) {
    // 修复：请求被取消时也需要清理 promotionReqQueue，避免队列阻塞结账功能
    if (response?.config?.headers?.['x-promotion-uid']) {
      store.dispatch('promotionReqQueue/actRemoveRequest', response.config.headers['x-promotion-uid'])
    }
    return Promise.reject({ code: cancelCode, msg: message, url: response?.config?.url })
  }
  // 处理客户端请求404错误
  let { status, data, statusText, config, headers } = response
  if (status === HttpStatusCode.NOT_FOUND) {
    data = { code: status, msg: `${statusText}【 ${config.url} 】` }
  }
  if (response.config?.headers?.['x-promotion-uid'])
    store.dispatch('promotionReqQueue/actRemoveRequest', response.config.headers['x-promotion-uid'])
  const { code, msg = statusText } = data || { code: 500, msg: i18n.t('本地网络异常，请检查网络情况') }
  //isOnline ? i18n.t('系统错误，请重试!'):

  switch (status) {
    // 无权限/登录失效
    case HttpStatusCode.UNAUTHORIZED:
    case HttpStatusCode.PAYMENT_REQUIRED:
      Vue.prototype.$mgMessage(msg, 'error')
      router.push({ name: '401' })
      break
    // 处理 handleResponseSuccess 中状态码不在successCodes中的情况
    case HttpStatusCode.NO_SUCCESS:
      // 根据指定的code，忽略全局错误弹窗，由业务页 catch 后自行处理
      if (shouldIgnoreErrorDialog(code, config)) return Promise.reject(response)
      break
    default:
      {
        if (shouldIgnoreErrorDialog(code, config)) return Promise.reject(response)
        if (httpErrorDialogCanShow) {
          httpErrorDialogCanShow = false

          let content = ''
          if(isOnline){
            content = msg
            //  if (status >= HttpStatusCode.ERROR_RESPONSE && status < HttpStatusCode.CACHE_SUCCESS) {
          if(status >= HttpStatusCode.ERROR_RESPONSE_400){
            content = msg + `<br/> ${i18n.t('错误ID')}： ${headers['x-rid'] ? headers['x-rid'].split(',')[0] : ''}`
          }
          }else{
            content = i18n.t('本地网络异常，请检查网络情况')
          }
          
         
          Vue.prototype.$mgConfirm(content, i18n.t('系统提示'), {
            dangerouslyUseHTMLString: true,
            showCancelButton: false,
            type: 'error',
            beforeClose(action, instance, done) {
              // 不要 MessageBox.close()：会清空 currentMsg，Promise 永不 settle，快捷键中心守卫卸不掉
              done()
              httpErrorDialogCanShow = true
              if (loginBackInCodes.includes(code)) {
                store.dispatch('user/logout').then(() => {
                  router.push({ name: 'login' })
                })
              }
            },
          })
        }
      }
      return Promise.reject(response)
  }
}

/**
 * 失败响应拦截 -- 用于语音播报
 */
export function handleSpeechResponseError(response) {
  const { data, config } = response
  // 特定接口语音播报
  if (speechApiLists?.includes(config?.url)) handleErrorVoiceSpeak(data?.msg || '')
  return Promise.reject(response)
}
